import React, { useState, useEffect } from "react";
import {
  FolderOpen,
  Server,
  Plus,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Copy,
  RefreshCw,
} from "lucide-react";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import { PrimaryButton } from "../common/ActionButtons";
import { sftpApi } from "../../utils/api";

const SFTPManagement = () => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedFacility, setExpandedFacility] = useState(null);
  const [showProvisionForm, setShowProvisionForm] = useState(false);
  const [provisionTarget, setProvisionTarget] = useState(null);
  const [provisionPassword, setProvisionPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const data = await sftpApi.getStatus();
      setFacilities(data);
    } catch (error) {
      console.error("Error fetching SFTP status:", error);
    }
    setLoading(false);
  };

  const handleProvision = async (e) => {
    e.preventDefault();
    if (!provisionTarget || !provisionPassword) return;
    setProvisioning(true);
    setProvisionResult(null);
    try {
      const result = await sftpApi.provision(
        provisionTarget.facility_id,
        provisionPassword,
      );
      if (result.success) {
        setProvisionResult({
          type: "success",
          message: `SFTP provisioned for ${provisionTarget.facility_id}`,
          details: result,
        });
        fetchStatus();
      } else {
        setProvisionResult({
          type: "error",
          message: result.error || "Provisioning failed",
        });
      }
    } catch (error) {
      setProvisionResult({
        type: "error",
        message: "Failed to connect to provisioning service",
      });
    }
    setProvisioning(false);
  };

  const openProvisionForm = (facility) => {
    setProvisionTarget(facility);
    setProvisionPassword("");
    setShowPassword(false);
    setProvisionResult(null);
    setShowProvisionForm(true);
  };

  const closeProvisionForm = () => {
    setShowProvisionForm(false);
    setProvisionTarget(null);
    setProvisionPassword("");
    setProvisionResult(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const filteredFacilities = facilities.filter((f) => {
    const matchesSearch =
      (f.facility_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (f.facility_id || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (f.sftp_username || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all"
        ? true
        : statusFilter === "provisioned"
          ? f.sftp_provisioned
          : !f.sftp_provisioned;
    return matchesSearch && matchesStatus;
  });

  const provisionedCount = facilities.filter((f) => f.sftp_provisioned).length;
  const unprovisionedCount = facilities.filter(
    (f) => !f.sftp_provisioned,
  ).length;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Facilities</p>
              <p className="text-2xl font-bold text-slate-900">
                {facilities.length}
              </p>
            </div>
            <Server className="w-8 h-8 text-slate-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">SFTP Provisioned</p>
              <p className="text-2xl font-bold text-green-600">
                {provisionedCount}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Not Provisioned</p>
              <p className="text-2xl font-bold text-amber-600">
                {unprovisionedCount}
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-amber-400" />
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onRefresh={fetchStatus}
            placeholder="Search facilities or SFTP users..."
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All</option>
            <option value="provisioned">Provisioned</option>
            <option value="unprovisioned">Not Provisioned</option>
          </select>
        </div>
      </div>

      {/* Provision form modal */}
      <FormModal
        title={`Provision SFTP — ${provisionTarget?.facility_id || ""}`}
        isOpen={showProvisionForm}
        onClose={closeProvisionForm}
      >
        <form onSubmit={handleProvision} className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
            <p>
              This will create SFTP directories and credentials for{" "}
              <span className="font-mono font-semibold">
                {provisionTarget?.facility_id}
              </span>{" "}
              ({provisionTarget?.facility_name}).
            </p>
            <p className="mt-1">
              SFTP username will be:{" "}
              <span className="font-mono font-semibold">
                {provisionTarget?.facility_id?.toLowerCase()}
              </span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              SFTP Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={provisionPassword}
                onChange={(e) => setProvisionPassword(e.target.value)}
                placeholder="Set SFTP password for this facility"
                className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
            <p className="font-medium">Directories to be created:</p>
            <ul className="mt-1 ml-4 list-disc">
              <li>
                <span className="font-mono">
                  /srv/hyve/documents/
                  {provisionTarget?.facility_id?.toLowerCase()}/
                  reference_documents/
                </span>
              </li>
              <li>
                <span className="font-mono">
                  /srv/hyve/documents/
                  {provisionTarget?.facility_id?.toLowerCase()}/
                  model_improvement_documents/
                </span>
              </li>
              <li>
                <span className="font-mono">
                  /srv/hyve/documents/processed/
                  {provisionTarget?.facility_id}/
                </span>
              </li>
            </ul>
          </div>

          {provisionResult && (
            <div
              className={`rounded-lg p-3 text-sm ${provisionResult.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
            >
              {provisionResult.message}
              {provisionResult.details?.api_key && (
                <div className="mt-2 p-2 bg-white rounded border border-green-200">
                  <p className="font-medium">API Key (save now):</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-xs break-all">
                      {provisionResult.details.api_key}
                    </code>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(provisionResult.details.api_key)
                      }
                      className="text-green-600 hover:text-green-800"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={closeProvisionForm}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={provisioning || !provisionPassword}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {provisioning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Provisioning...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Provision SFTP
                </>
              )}
            </button>
          </div>
        </form>
      </FormModal>

      {/* Facility list */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">
          Loading SFTP status...
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">
                  Facility
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">
                  SFTP User
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">
                  UID / GID
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">
                  Vertical
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredFacilities.map((f) => {
                const isExpanded = expandedFacility === f.facility_id;
                return (
                  <React.Fragment key={f.id}>
                    <tr
                      className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isExpanded ? "bg-blue-50" : ""}`}
                      onClick={() =>
                        setExpandedFacility(isExpanded ? null : f.facility_id)
                      }
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400 mr-2" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400 mr-2" />
                          )}
                          <div>
                            <div className="font-medium text-slate-900">
                              {f.facility_name}
                            </div>
                            <div className="text-xs font-mono text-slate-500">
                              {f.facility_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-sm text-slate-700">
                        {f.sftp_username || (
                          <span className="text-slate-400 italic">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {f.sftp_uid ? (
                          <span className="font-mono">
                            {f.sftp_uid} / {f.sftp_gid}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {f.sftp_provisioned ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Provisioned
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Not Provisioned
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 capitalize">
                        {f.vertical_id || "--"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!f.sftp_provisioned && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openProvisionForm(f);
                            }}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Provision
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 bg-slate-50">
                          <div className="grid grid-cols-2 gap-6">
                            {/* SFTP Details */}
                            <div>
                              <h4 className="text-sm font-semibold text-slate-700 mb-3">
                                SFTP Details
                              </h4>
                              {f.sftp_provisioned ? (
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Host</span>
                                    <div className="flex items-center gap-1">
                                      <span className="font-mono">
                                        78.138.51.30:2222
                                      </span>
                                      <button
                                        onClick={() =>
                                          copyToClipboard("78.138.51.30")
                                        }
                                        className="text-slate-400 hover:text-slate-600"
                                      >
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">
                                      Username
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <span className="font-mono">
                                        {f.sftp_username}
                                      </span>
                                      <button
                                        onClick={() =>
                                          copyToClipboard(f.sftp_username)
                                        }
                                        className="text-slate-400 hover:text-slate-600"
                                      >
                                        <Copy className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">
                                      UID / GID
                                    </span>
                                    <span className="font-mono">
                                      {f.sftp_uid} / {f.sftp_gid}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">
                                      Contact
                                    </span>
                                    <span>{f.contact_email || "--"}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">
                                      Onboarded
                                    </span>
                                    <span>
                                      {f.onboarded_at
                                        ? new Date(
                                            f.onboarded_at,
                                          ).toLocaleDateString()
                                        : "--"}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-slate-500 italic">
                                  SFTP not provisioned yet.
                                  <button
                                    onClick={() => openProvisionForm(f)}
                                    className="ml-2 text-blue-600 hover:underline"
                                  >
                                    Provision now
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Directory Structure */}
                            <div>
                              <h4 className="text-sm font-semibold text-slate-700 mb-3">
                                Directory Structure
                              </h4>
                              {f.sftp_provisioned ? (
                                <div className="bg-slate-900 rounded-lg p-3 text-xs font-mono text-green-400 space-y-1">
                                  <div className="text-slate-500">
                                    # SFTP home (chroot)
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <FolderOpen className="w-3 h-3 text-blue-400 inline" />
                                    <span>{f.sftp_home}/</span>
                                  </div>
                                  {f.sftp_subdirs.map((sub) => (
                                    <div
                                      key={sub}
                                      className="ml-4 flex items-center gap-1"
                                    >
                                      <FolderOpen className="w-3 h-3 text-yellow-400 inline" />
                                      <span>{sub}/</span>
                                    </div>
                                  ))}
                                  <div className="text-slate-500 mt-2">
                                    # Processed output
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <FolderOpen className="w-3 h-3 text-purple-400 inline" />
                                    <span>
                                      /srv/hyve/documents/processed/
                                      {f.facility_id}/
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-slate-500 italic">
                                  No directories created.
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filteredFacilities.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    {searchTerm || statusFilter !== "all"
                      ? "No facilities match your filters."
                      : "No facilities found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SFTPManagement;
